// Client-portal data layer.
// Reads/writes go straight to Supabase under RLS (a client only ever sees their
// own rows). Payment creation + AI PM go through the backend server (Doku HMAC,
// OpenRouter). Locally the server trusts loopback; for remote we still attach the
// Supabase access token so the server can verify the caller.
import { supabase } from '../supabaseClient';

const SERVER_URL = (process.env.REACT_APP_SERVER_URL || 'http://localhost:3001').replace(/\/$/, '');

async function serverFetch(path, { method = 'GET', body } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch (_) { /* unauthenticated is fine for some calls */ }

  const res = await fetch(`${SERVER_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    throw new Error(json.error || `Request failed (${res.status})`);
  }
  return json;
}

export const formatIDR = (n) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(Number(n) || 0);

const genOrderNumber = () => {
  const d = new Date();
  const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  return `ORD-${ymd}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
};

// --- Orders ---------------------------------------------------------------
export async function createOrder({ pkg, plan, total }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Please sign in first');
  const row = {
    order_number: genOrderNumber(),
    client_id: user.id,
    client_name: user.user_metadata?.full_name || user.user_metadata?.name || user.email,
    client_email: user.email,
    package_id: pkg?.id ?? null,
    package_name: pkg?.name || 'Custom project',
    category: pkg?.category || null,
    total_amount: Number(total) || Number(pkg?.price) || 0,
    payment_plan: plan || 'full',
    status: 'brief_pending',
  };
  const { data, error } = await supabase.from('orders').insert(row).select('*').single();
  if (error) throw error;
  return data;
}

export async function getMyOrders() {
  const { data, error } = await supabase
    .from('orders').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getOrder(orderId) {
  const { data, error } = await supabase.from('orders').select('*').eq('id', orderId).single();
  if (error) throw error;
  return data;
}

export async function updateOrder(orderId, patch) {
  const { data, error } = await supabase.from('orders').update(patch).eq('id', orderId).select('*').single();
  if (error) throw error;
  return data;
}

// --- Milestones -----------------------------------------------------------
export async function getMilestones(orderId) {
  const { data, error } = await supabase
    .from('order_milestones').select('*').eq('order_id', orderId).order('sequence', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function createMilestones(orderId, milestones) {
  const rows = milestones.map((m, i) => ({
    order_id: orderId,
    sequence: m.sequence ?? i + 1,
    title: m.title,
    description: m.description || null,
    amount: Number(m.amount) || 0,
    due_date: m.due_date || null,
    status: 'pending',
  }));
  const { data, error } = await supabase.from('order_milestones').insert(rows).select('*');
  if (error) throw error;
  return data || [];
}

// --- Payments / checkout --------------------------------------------------
export async function startCheckout({ orderId, plan, milestoneId }) {
  const returnUrl = `${window.location.origin}/payment/return`;
  return serverFetch('/api/doku/checkout', {
    method: 'POST',
    body: { orderId, plan, milestoneId, returnUrl },
  });
}

export async function getPayments(orderId) {
  const { data, error } = await supabase
    .from('payments').select('*').eq('order_id', orderId).order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getPaymentByDokuId(dokuOrderId) {
  const { data } = await supabase.from('payments').select('*, orders(*)').eq('doku_order_id', dokuOrderId).maybeSingle();
  return data || null;
}

export async function getDokuStatus(dokuOrderId) {
  return serverFetch(`/api/doku/status?order_id=${encodeURIComponent(dokuOrderId)}`);
}

// --- Invoices -------------------------------------------------------------
export async function getInvoices(orderId) {
  const { data, error } = await supabase
    .from('invoices').select('*').eq('order_id', orderId).order('issued_at', { ascending: false });
  if (error) throw error;
  return data || [];
}
export const invoiceUrl = (ref) => `${SERVER_URL}/api/invoice/${encodeURIComponent(ref)}`;

// --- Project brief + AI PM ------------------------------------------------
export async function getBrief(orderId) {
  const { data } = await supabase.from('project_briefs').select('*').eq('order_id', orderId).maybeSingle();
  return data || null;
}

export async function saveBrief(orderId, content) {
  const existing = await getBrief(orderId);
  if (existing) {
    const { data, error } = await supabase
      .from('project_briefs').update({ content }).eq('id', existing.id).select('*').single();
    if (error) throw error;
    return data;
  }
  const { data: { user } } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from('project_briefs').insert({ order_id: orderId, client_id: user?.id, content }).select('*').single();
  if (error) throw error;
  return data;
}

export async function generatePlan({ orderId, brief, packageName, total, plan, status }) {
  return serverFetch('/api/pm/plan', {
    method: 'POST',
    body: { orderId, brief, packageName, total, plan, status },
  });
}

export async function getPMUpdates(orderId) {
  const { data, error } = await supabase
    .from('pm_updates').select('*').eq('order_id', orderId).order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function postPMMessage(orderId, content) {
  const { data, error } = await supabase
    .from('pm_updates').insert({ order_id: orderId, role: 'client', kind: 'message', content }).select('*').single();
  if (error) throw error;
  return data;
}

// --- Deliverables (receive result) ---------------------------------------
export async function getDeliverables(orderId) {
  const { data, error } = await supabase
    .from('deliverables').select('*').eq('order_id', orderId).order('delivered_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function markDeliverableReceived(deliverableId) {
  const { data, error } = await supabase
    .from('deliverables').update({ received_at: new Date().toISOString() }).eq('id', deliverableId).select('*').single();
  if (error) throw error;
  return data;
}

export const PLAN_LABELS = {
  full: 'Pay in full',
  dp50: 'Down payment 50%',
  milestone: 'Per milestone',
};

export const STATUS_LABELS = {
  brief_pending: 'Brief needed',
  awaiting_payment: 'Awaiting payment',
  in_progress: 'In progress',
  review: 'In review',
  completed: 'Completed',
  cancelled: 'Cancelled',
};
